/**
 * MST3K-style silhouette placement (PRD §8). Pure geometry: given a participant
 * count and a canvas size, return where to draw each segmented person, where to
 * print each name, and a single box framing the whole group (the "theater
 * balcony"). The actual segmentation + canvas compositing lives in
 * `compositor.ts` (DOM) and is covered by the e2e smoke test.
 *
 * The silhouettes are clustered together (a contiguous row), the cluster is
 * centered along the bottom, each has a name slot beneath it, and the group box
 * wraps the lot.
 */
export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Label {
  /** Horizontal center for the (center-aligned) name text. */
  x: number;
  /** Text baseline y. */
  baseline: number;
  /** Max text width before the engine should compress it. */
  maxWidth: number;
}

export interface SilhouettePlacement {
  silhouettes: Rect[];
  labels: Label[];
  /** Frame around the whole cluster; zero-size when there are no participants. */
  groupBox: Rect;
  /** Suggested name font size in px (tracks any shrink-to-fit). */
  fontPx: number;
}

export interface LayoutOptions {
  /** Silhouette height as a fraction of canvas height. */
  heightFrac: number;
  /** Silhouette width / height (portrait). */
  aspect: number;
  /** Gap between adjacent silhouettes, fraction of canvas height. */
  gapFrac: number;
  /** Height of the name slot beneath each silhouette, fraction of canvas height. */
  labelFrac: number;
  /** Inner padding of the group box, fraction of canvas height. */
  padFrac: number;
  /** Gap from the canvas bottom to the box, fraction of canvas height. */
  bottomMarginFrac: number;
}

export const DEFAULT_LAYOUT: LayoutOptions = {
  heightFrac: 0.3,
  aspect: 0.6,
  gapFrac: 0.03,
  labelFrac: 0.06,
  padFrac: 0.025,
  bottomMarginFrac: 0.03,
};

const ZERO: Rect = { x: 0, y: 0, w: 0, h: 0 };

export function silhouetteLayout(
  n: number,
  canvasW: number,
  canvasH: number,
  opts: LayoutOptions = DEFAULT_LAYOUT,
): SilhouettePlacement {
  if (n <= 0) return { silhouettes: [], labels: [], groupBox: { ...ZERO }, fontPx: 0 };

  let h = canvasH * opts.heightFrac;
  let w = h * opts.aspect;
  let gap = canvasH * opts.gapFrac;
  let label = canvasH * opts.labelFrac;
  const pad = canvasH * opts.padFrac;

  // Shrink the cluster (keeping aspect) if it would overflow the canvas width.
  const maxBoxW = canvasW * 0.96;
  let boxW = n * w + (n - 1) * gap + 2 * pad;
  if (boxW > maxBoxW) {
    const s = (maxBoxW - 2 * pad) / (n * w + (n - 1) * gap);
    w *= s;
    h *= s;
    gap *= s;
    label *= s;
    boxW = n * w + (n - 1) * gap + 2 * pad;
  }

  const boxH = h + label + 2 * pad;
  const boxX = (canvasW - boxW) / 2;
  const boxBottom = canvasH - canvasH * opts.bottomMarginFrac;
  const boxY = boxBottom - boxH;

  const sx0 = boxX + pad;
  const sy = boxY + pad;

  const silhouettes: Rect[] = [];
  const labels: Label[] = [];
  for (let i = 0; i < n; i++) {
    const x = sx0 + i * (w + gap);
    silhouettes.push({ x, y: sy, w, h });
    labels.push({
      x: x + w / 2,
      baseline: sy + h + label * 0.72,
      maxWidth: w + gap,
    });
  }

  return {
    silhouettes,
    labels,
    groupBox: { x: boxX, y: boxY, w: boxW, h: boxH },
    fontPx: Math.max(10, label * 0.7),
  };
}
